import type { ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Os ícones do menu, mais a moldura que todos os ícones da aplicação usam.
 *
 * Este arquivo e o `Icones.tsx` são o mesmo conjunto partido em dois porque 23 ícones não
 * cabem no teto de 200 linhas — e desligar a regra para uma lista longa é como o sistema
 * anterior chegou a uma Blade de 1119 linhas. Importe sempre por `components/Icones`, que
 * reexporta tudo o que está aqui; a divisão é detalhe de arquivo, não de uso.
 *
 * A moldura mora neste arquivo, e não no outro, só para a dependência ter um sentido único:
 * `Icones` importa daqui, nunca o contrário. Duas peças que se importam mutuamente funcionam
 * até alguém acrescentar código que roda na carga do módulo.
 */

export interface PropsDeIcone {
  /** Tamanho e cor saem daqui. O padrão é `h-5 w-5`, legível a 20px. */
  className?: string
  /**
   * Só quando o ícone é a ÚNICA coisa que o controle mostra.
   *
   * Ícone ao lado de texto é decoração e tem de ficar invisível ao leitor de tela — senão a
   * pessoa ouve "imagem, gráfico, Imprimir" num botão escrito "Imprimir". Por isso o padrão é
   * `aria-hidden` e o nome acessível é a exceção, declarada de fora.
   */
  titulo?: string
}

/**
 * Moldura comum: `viewBox`, traço e as decisões de acessibilidade num lugar só.
 *
 * Todos herdam `currentColor`: a cor do ícone é a do elemento em volta, e não existe um
 * segundo lugar para lembrar de trocar quando o botão muda de cor.
 *
 * @param props classe, nome acessível opcional e o desenho
 * @returns o SVG montado
 */
export function Icone({ className, titulo, children }: PropsDeIcone & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('h-5 w-5 shrink-0', className)}
      role={titulo ? 'img' : undefined}
      aria-hidden={titulo ? undefined : true}
    >
      {titulo && <title>{titulo}</title>}
      {children}
    </svg>
  )
}

/** Painel inicial: os blocos de resumo. */
export function IconePainel(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </Icone>
  )
}

/** Solicitação de mudança: duas setas trocando de lugar. */
export function IconeMudanca(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M3 8h14" />
      <path d="M13.5 4.5 17 8l-3.5 3.5" />
      <path d="M21 16H7" />
      <path d="M10.5 12.5 7 16l3.5 3.5" />
    </Icone>
  )
}

/** Abrir um registro novo. */
export function IconeNovo(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </Icone>
  )
}

/** Quadro de acompanhamento, em colunas. */
export function IconeQuadro(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
      <path d="M15 4v16" />
    </Icone>
  )
}

/** Comitê de Mudanças: a mesa e quem senta nela. */
export function IconeComite(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="4.2" r="1.6" />
      <circle cx="18.8" cy="16" r="1.6" />
      <circle cx="5.2" cy="16" r="1.6" />
    </Icone>
  )
}

/** Calendário: pauta do comitê e janela de implantação. */
export function IconeCalendario(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
      <path d="M3 10h18" />
    </Icone>
  )
}

/** Chamado do e-Niterói. */
export function IconeChamado(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M14.5 6v1.5M14.5 10.5v3M14.5 16.5V18" />
      <path d="M7 11h4M7 14h3" />
    </Icone>
  )
}

/** Ordem de serviço. */
export function IconeOrdemServico(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M6 3h8l5 5v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h4" />
    </Icone>
  )
}

/** Indicador: a linha que sobe. */
export function IconeIndicador(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M3 21h18" />
      <path d="M4 17l5-6 3.5 3.5L20 7" />
      <path d="M15 7h5v5" />
    </Icone>
  )
}

/** Relatórios: a pasta com os documentos do contrato — era uma folha igual à da ordem de serviço. */
export function IconeRelatorio(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
      <path d="M8 13h8M8 16h5" />
    </Icone>
  )
}

/** Pessoas: cadastro de usuários e papéis. */
export function IconeUsuarios(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <circle cx="9.5" cy="8" r="3.5" />
      <path d="M3 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16.5 5.2a3 3 0 0 1 0 5.6" />
      <path d="M18 14.2A5.5 5.5 0 0 1 21 19" />
    </Icone>
  )
}

/** Configuração: os controles deslizantes. */
export function IconeConfiguracao(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M4 7h9M18 7h2" />
      <path d="M4 17h5M14 17h6" />
      <circle cx="15.5" cy="7" r="2.5" />
      <circle cx="11.5" cy="17" r="2.5" />
    </Icone>
  )
}

/** Sair da sessão. */
export function IconeSair(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M12 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h6" />
      <path d="M16 16l4-4-4-4" />
      <path d="M20 12H9" />
    </Icone>
  )
}

/** Abrir o menu em tela pequena. */
export function IconeMenu(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Icone>
  )
}

/** Fechar painel, diálogo ou aviso. */
export function IconeFechar(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Icone>
  )
}
